import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material';
import { useData } from '../../context/DataContext';
import { downloadResume } from '../../utils/fileUpload';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  AttachMoney as SalaryIcon,
  Schedule as NoticeIcon,
  Source as SourceIcon,
  Description as CoverLetterIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';

const ManageJobApplications = () => {
  const { state, dispatch } = useData();
  const { jobApplications, jobs } = state;

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    jobId: '',
    status: '',
    department: ''
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedApplication(null);
  };

  const handleDownloadResume = async (application) => {
    if (!application?.id) {
      window.alert('Application ID not found. Please refresh and try again.');
      return;
    }

    try {
      await downloadResume(application.id, application.resumeFilename || 'resume.pdf');
    } catch (error) {
      window.alert(error.message || 'Failed to download resume. Please try again.');
    }
  };

  const handleStatusChange = (applicationId, newStatus) => {
    dispatch({ type: 'UPDATE_JOB_APPLICATION_STATUS', payload: { id: applicationId, status: newStatus } });
  };

  const hasResumeFile = (application) =>
    Boolean(application?.hasResume || application?.resumeFilename);

  const formatAppliedDate = (createdAt) => {
    if (!createdAt) return '—';
    const date = new Date(createdAt);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  };

  const filteredApplications = jobApplications.filter(app => {
    if (filters.jobId && app.jobId !== filters.jobId) return false;
    if (filters.status && app.status !== filters.status) return false;
    if (filters.department) {
      const job = jobs.find(j => j.id === app.jobId);
      if (!job || job.department !== filters.department) return false;
    }
    return true;
  });

  const departments = [...new Set(jobs.map(job => job.department))];
  const jobOptions = jobs.map(job => ({ id: job.id, title: job.title }));

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'shortlisted': return 'primary';
      case 'rejected': return 'error';
      case 'hired': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Job Applications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Total Applications: {jobApplications.length}
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Job Position</InputLabel>
                <Select
                  value={filters.jobId}
                  label="Job Position"
                  onChange={(e) => handleFilterChange('jobId', e.target.value)}
                >
                  <MenuItem value="">All Jobs</MenuItem>
                  {jobOptions.map(job => (
                    <MenuItem key={job.id} value={job.id}>{job.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={filters.department}
                  label="Department"
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="hired">Hired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Applied Date</TableCell>
              <TableCell>Resume</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApplications.map((application) => {
              const job = jobs.find(j => String(j.id) === String(application.jobId));
              const positionTitle = job?.title || application.jobTitle || (application.jobId ? 'Unknown Position' : 'General Application');
              const departmentLabel = job?.department || application.jobDepartment || application.department || 'Unknown';
              return (
                <TableRow key={application.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {application.firstName} {application.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {positionTitle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={departmentLabel} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.experience}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={application.status} 
                      size="small" 
                      color={getStatusColor(application.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {formatAppliedDate(application.createdAt)}
                  </TableCell>
                  <TableCell>
                    {hasResumeFile(application) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PdfIcon color="error" fontSize="small" />
                        <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {application.resumeFilename}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No resume
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewApplication(application)}
                      color="primary"
                      title="View Application"
                    >
                      <ViewIcon />
                    </IconButton>
                    {hasResumeFile(application) && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadResume(application)}
                        color="secondary"
                        title="Download Resume"
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Application Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Application Details
            </Typography>
            {selectedApplication && (
              <Typography variant="body1" color="text.secondary">
                {selectedApplication.firstName} {selectedApplication.lastName} - {jobs.find(j => String(j.id) === String(selectedApplication.jobId))?.title || selectedApplication.jobTitle || 'General Application'}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedApplication && (
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Personal Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Name:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.firstName} {selectedApplication.lastName}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon fontSize="small" color="primary" />
                  <Typography variant="body2">{selectedApplication.email}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon fontSize="small" color="primary" />
                  <Typography variant="body2">{selectedApplication.phone}</Typography>
                </Box>
              </Grid>

              {/* Professional Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Professional Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WorkIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Current Position:</Typography>
                  <Typography variant="body2">{selectedApplication.currentPosition}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WorkIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Current Company:</Typography>
                  <Typography variant="body2">{selectedApplication.currentCompany || 'Not specified'}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WorkIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Experience:</Typography>
                  <Typography variant="body2">{selectedApplication.experience}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SchoolIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Education:</Typography>
                  <Typography variant="body2">{selectedApplication.education}</Typography>
                </Box>
              </Grid>

              {/* Application Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Application Details
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SalaryIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Expected Salary:</Typography>
                  <Typography variant="body2">{selectedApplication.expectedSalary}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <NoticeIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Notice Period:</Typography>
                  <Typography variant="body2">{selectedApplication.noticePeriod}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SourceIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Source:</Typography>
                  <Typography variant="body2">{selectedApplication.source || 'Not specified'}</Typography>
                </Box>
              </Grid>

              {/* Resume */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Resume
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                {hasResumeFile(selectedApplication) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PdfIcon color="error" fontSize="large" />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {selectedApplication.resumeFilename || 'Resume.pdf'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resume uploaded on {formatAppliedDate(selectedApplication.createdAt)}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadResume(selectedApplication)}
                      sx={{ ml: 'auto' }}
                    >
                      Download Resume
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    No resume file uploaded with this application.
                  </Alert>
                )}
              </Grid>

              {/* Cover Letter */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Cover Letter
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#f5f5f5', 
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedApplication.coverLetter}
                  </Typography>
                </Box>
              </Grid>

              {/* Status Management */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Application Status
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={selectedApplication.status}
                    label="Update Status"
                    onChange={(e) => handleStatusChange(selectedApplication.id, e.target.value)}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="reviewed">Reviewed</MenuItem>
                    <MenuItem value="shortlisted">Shortlisted</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="hired">Hired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
          <Button 
            variant="contained"
            startIcon={<EmailIcon />}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageJobApplications; 